// ── Scheduling Module Types ──
// Projection layer only — does NOT modify core Job or FIE entities

export type EventType = "install" | "inspection" | "followup" | "delivery" | "consultation" | "sales_appointment" | "service_call" | "pre_install";
export type EventStatus = "draft" | "scheduled" | "confirmed" | "in_progress" | "completed" | "cancelled";
export type CrewType = "internal" | "subcontractor";

/** Departments that events belong to — used for role-based calendar filtering */
export type ScheduleDepartment = "install" | "sales" | "warehouse" | "service" | "operations";

export interface ScheduledEvent {
  id: string;
  jobId: string;
  eventType: EventType;
  title: string;
  startTime: string; // ISO
  endTime: string;   // ISO
  crewId: string;
  crewName: string;
  crewType: CrewType;
  status: EventStatus;
  notes?: string;
  /** The user/rep this event is assigned to (for "My Schedule" filtering) */
  assignedTo?: string;
  department: ScheduleDepartment;
  createdAt: string;
  updatedAt: string;
}

export interface CrewMember {
  id: string;
  name: string;
  type: CrewType;
  color: string; // for calendar display
  active: boolean;
}

export interface SchedulingConflict {
  eventA: string;
  eventB: string;
  crewId: string;
  type: "overlap" | "back_to_back";
}

export interface DaySlot {
  date: string; // YYYY-MM-DD
  events: ScheduledEvent[];
  conflicts: SchedulingConflict[];
}

export const EVENT_TYPE_LABELS: Record<EventType, string> = {
  install: "Installation",
  inspection: "Inspection",
  followup: "Follow-up",
  delivery: "Delivery",
  consultation: "Consultation",
  sales_appointment: "Sales Appointment",
  service_call: "Service Call",
  pre_install: "Pre-Install",
};

export const EVENT_STATUS_LABELS: Record<EventStatus, string> = {
  draft: "Draft",
  scheduled: "Scheduled",
  confirmed: "Confirmed",
  in_progress: "In Progress",
  completed: "Completed",
  cancelled: "Cancelled",
};

/** Maps event types to their owning department */
export const EVENT_TYPE_DEPARTMENT: Record<EventType, ScheduleDepartment> = {
  install: "install",
  inspection: "install",
  pre_install: "install",
  followup: "sales",
  consultation: "sales",
  sales_appointment: "sales",
  delivery: "warehouse",
  service_call: "service",
};

export const DEPARTMENT_LABELS: Record<ScheduleDepartment, string> = {
  install: "Install & Ops",
  sales: "Sales",
  warehouse: "Warehouse",
  service: "Service",
  operations: "All Operations",
};

export const DEPARTMENT_COLORS: Record<ScheduleDepartment, string> = {
  install: "hsl(207 80% 42%)",
  sales: "hsl(152 60% 38%)",
  warehouse: "hsl(24 90% 52%)",
  service: "hsl(280 60% 50%)",
  operations: "hsl(220 14% 50%)",
};
